import { defineStore } from "pinia";

import { computed, ref } from "vue";
import dayjs from "dayjs";

import { Estimate } from "@/app/domain";
import { EstimateConverterService } from "@/app/services/legasy-estimate-converter.service";
import { DimensionValue } from "@/app/domain/entities/dimensions";
import { getTitleFromDictByCode, useDictStore } from "../dictStore";
import {
    getEstimate,
    makeEstimateDefaultData,
    postEstimate,
    updateUser,
} from "@/utils/api";
import { useUserStore } from "../userStore";
import { SummaryService } from "@/services/summaryService";
import { useSpacesStore } from "./spaces.store";
import { useDeliveryStore } from "./delivery.store";
import { useEstimateInfo } from "@/app/composables";
import { useRoute, useRouter } from "vue-router";

const LOCAL_STORAGE_ESTIMATE_KEY = "estimate";

export const useNewEstimateStore = defineStore(
    "new-estimate",
    () => {
        const dictStore = useDictStore();
        const userStore = useUserStore();
        const deliveryStore = useDeliveryStore();
        const spacesStore = useSpacesStore();

        const router = useRouter();
        const route = useRoute();

        const estimate = ref<Estimate>(makeEstimateDefaultData());

        const activeSpace = computed(() => spacesStore.activeSpace);
        const spaces = computed(() => spacesStore.spaces);
        const profile = computed(() => userStore.profile);

        useEstimateInfo();
        const $reset = () => {
            estimate.value = makeEstimateDefaultData();
            spacesStore.$reset();
        };

        const renameEstimate = (title = "") => {
            if (!title && !estimate.value.title) {
                estimate.value.title = generateEstimateTitle();
            } else {
                estimate.value.title = title;
            }
        };

        const generateEstimateTitle = () => {
            return [
                estimate.value.client.name,
                estimate.value.installedSystemCode
                    ? getTitleFromDictByCode(
                          dictStore.getSystemTypes(),
                          estimate.value.installedSystemCode,
                      )
                    : "",
                estimate.value.shippingZip,
                estimate.value.shippingState,
            ]
                .filter((item) => (item ? item.trim() : false))
                .join(", ");
        };

        const load = async (estimateCode: string, force = false) => {
            // TODO: REFACTOR THIS
            if (force) {
                const estimateData = await getEstimate(estimateCode);
                useDictStore().loadAllDictsByDate(
                    estimateData.estimate?.info?.priceDateFormated,
                    estimateData.estimate?.info?.priceDate,
                );

                estimate.value = estimateData.estimate.info;
                estimate.value.progress = estimateData.estimate.progress;
                estimate.value.progress.isSaved = true;
            } else {
                const storedEstimate = JSON.parse(
                    localStorage.getItem(LOCAL_STORAGE_ESTIMATE_KEY),
                );
                if (
                    storedEstimate &&
                    storedEstimate.info.code === estimateCode
                ) {
                    return;
                }
            }
            // estimate.value.progress. = estimateCode;
        };

        const getActiveEstimateCode = () => {
            return estimate.value.code;
        };

        const getEstimateReadonly = () => {
            const progress = estimate.value.progress;
            const roles = profile.value.roles || [];

            if (!profile.value || !roles.length || !progress) {
                return true;
            }

            if (
                progress.status === "DRAFT" ||
                progress.status === "DONE" ||
                progress.status === "QUOTE"
            ) {
                return false;
            }

            const estimateInfo = estimate.value;
            const estimateStatus = estimate.value?.progress?.status;
            const userEmail = profile.value?.email;

            if (roles.some((role) => role.slug === "admin")) {
                return false;
            }

            // Если пользователь менеджер и совпадает email
            if (roles.some((role) => role.slug === "manager")) {
                if (!estimateInfo?.managerCode || !userEmail) {
                    return true;
                }
                return estimateInfo.managerCode !== userEmail;
            }

            // Если пользователь оценщик и статус ORDER
            if (
                roles.some((role) => role.slug === "estimator") &&
                estimateStatus === "ORDER"
            ) {
                return false;
            }

            return true;
        };

        const updateClientInfo = async () => {
            const updatedUserData = {
                name: estimate.value.client.name,
                billing_address: estimate.value.client.billingAddress,
                state: estimate.value.client.billingState,
                zip_code: estimate.value.client.billingZip,
                phone: estimate.value.client.phone,
                email: estimate.value.client.email,
                business_name: estimate.value.client.businessName,
                manager_id: estimate.value.managerCode?.id,
            };

            try {
                await updateUser(updatedUserData);
            } catch (error) {
                console.error("Failed to update client info", error);
            }
        };

        const save = async (status?: string) => {
            const spaces = computed(() => spacesStore.spaces);
            const delivery = computed(() => estimate.value.delivery);
            const deliveryItems = computed(() => deliveryStore.deliveryItems);

            const summary = SummaryService.fromEstimate(
                estimate.value,
                spaces.value,
                deliveryItems.value,
            );

            const isNew = !estimate.value.progress.isSaved;

            estimate.value.progress.status = status || "DRAFT";
            estimate.value.progress.isSaved = true;
            estimate.value.progress.updated = dayjs().valueOf().toString();

            const savingData = {
                estimate: EstimateConverterService.convertToOldFormat(
                    estimate.value,
                    spaces.value,
                    delivery.value,
                ),
                calculation:
                    EstimateConverterService.convertCalculationToOldFormat(
                        estimate.value,
                        spaces.value,
                    ),
                summary: summary,
                header: EstimateConverterService.convertToOldBackedHeader(
                    estimate.value,
                ),
            };
            console.log("SAVING", savingData.estimate);
            try {
                await postEstimate(savingData, estimate.value.code, isNew);
                estimate.value.progress.isSaved = true;

                router.push({
                    path: route.path,
                    params: route.params,
                    query: { estimateCode: estimate.value.code },
                });
            } catch (error) {
                console.error(error);
                estimate.value.progress.isSaved = !isNew;
            }
        };

        const updateProgressInfo = (data: object): void => {
            console.debug("updateProgressInfo", data);
            estimate.value.progress = { ...estimate.value.progress, ...data };
        };

        const calculateTotalSquareFt = () => {
            if (!activeSpace.value) {
                return;
            }

            const totalSqFt = spaces.value.reduce((total, space) => {
                const spaceTotalSqFt = space
                    .calculateTotalAreaSqFt()
                    .getValue();

                return total + spaceTotalSqFt;
            }, 0);

            estimate.value.totalSquareFt = new DimensionValue(totalSqFt);
        };

        const $restoreEstimate = (estimateInfo: any) => {
            estimate.value = estimateInfo;
        };

        return {
            estimate,
            $reset,
            $restoreEstimate,
            renameEstimate,
            load,
            getActiveEstimateCode,
            getEstimateReadonly,
            save,
            updateProgressInfo,
            calculateTotalSquareFt,
            updateClientInfo,
        };
    },
    { persist: false },
);
